// pfs-paginator.ts — Seiten-Navigationsleiste fuer eine Liste (PFS-632).
//
// Reine VIEW auf einen ListController: erste/vorherige/"Seite X von Y"/naechste/
// letzte, optional Seitengroessen-Waehler. Bindet an DENSELBEN
// Controller-Kontrakt wie der <pfs-data-table>-Infinite-Scroll → Autoscroll gegen
// Paginationsleiste tauschen = dieses Element mounten statt data-has-more zu
// setzen, KEINE Fetch-Umverdrahtung (PFS-632 "N Optiken, ein Controller").
//
// Bindung (per JS):
//   el.controller = ListController   — der Paginator subscribed und re-rendert bei
//                                      jeder Zustandsaenderung, ruft die Ops zurueck.
//
// Attribute (data-*):
//   data-page-sizes   "10,25,50,100" → Seitengroessen-Select (setLimit).
//   data-template     Vorlage der Positionsanzeige, Default "Seite {page} von {pages}".
//   data-label-first/-prev/-next/-last/-all/-size  — Beschriftungs-Overrides (i18n
//                     folgt via PFS-418; Defaults sind Deutsch/Symbole).
//
// Generisch (@pfs/components-Boundary): keine Domain-Texte; Optik token-basiert
// im eigenen Shadow (brandbar/dark, immun gegen WP/Tailwind-Bleed).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";

// Struktureller Kontrakt — genau das, was der Paginator vom Controller braucht.
// Absichtlich schmal (nicht der ganze ListController): jede Sicht, die diesen
// Zustand liefert + die Ops erfuellt, ist bindbar.
export interface PaginatorController {
  readonly currentPage: number;
  readonly pageCount: number;
  readonly hasPrev: boolean;
  readonly hasNext: boolean;
  readonly limit: number;
  readonly loading: boolean;
  firstPage(): Promise<void>;
  prevPage(): Promise<void>;
  nextPage(): Promise<void>;
  lastPage(): Promise<void>;
  setLimit(n: number): Promise<void>;
  subscribe(fn: () => void): () => void;
}

/** Typisierte Consumer-Sicht auf ein <pfs-paginator>-Element — Consumer setzen
 *  `(createElement("pfs-paginator") as PfsPaginatorElement).controller = ctrl`. */
export interface PfsPaginatorElement extends HTMLElement {
  controller: PaginatorController;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; }
.pg {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.25rem;
  font-size: 0.85rem;
  color: var(--pfs-color-on-surface-muted);
  flex-wrap: wrap;
}
${BUTTON_CSS}
.btn { min-width: 2rem; }
.pg-pos { padding: 0 0.4rem; white-space: nowrap; }
.pg-spacer { flex: 1 1 auto; }
.pg-size { font: inherit; padding: 0.2rem 0.4rem; border-radius: var(--pfs-radius-sm, 0.375rem); border: 1px solid var(--pfs-color-outline-strong); background: var(--pfs-color-surface); color: var(--pfs-color-on-surface); }
`;

defineComponent({
  tagName: "pfs-paginator",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const box = document.createElement("div");
    root.appendChild(box);

    let controller: PaginatorController | null = null;
    let unsub: (() => void) | null = null;

    const self = host as PfsPaginatorElement;
    const preController = takeOwnProperty<PaginatorController>(host, "controller");

    const label = (suffix: string, fallback: string): string =>
      host.dataset[("label" + suffix) as keyof DOMStringMap] || fallback;

    const btn = (text: string, disabled: boolean, op: () => unknown, aria: string): HTMLButtonElement => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "btn secondary sm";
      b.textContent = text;
      b.disabled = disabled;
      b.setAttribute("aria-label", aria);
      b.addEventListener("click", () => {
        if (!controller || controller.loading) return;
        void op();
      });
      return b;
    };

    const paint = (): void => {
      box.textContent = "";
      const c = controller;
      const pageSizesRaw = host.dataset.pageSizes;
      // Ohne Controller oder wenn nichts zu blaettern/waehlen ist → leer.
      if (!c || (c.pageCount <= 1 && !pageSizesRaw)) return;

      const nav = document.createElement("div");
      nav.className = "pg";
      nav.setAttribute("role", "navigation");

      const busy = c.loading;
      nav.appendChild(btn(label("First", "«"), busy || !c.hasPrev, () => c.firstPage(), "Erste Seite"));
      nav.appendChild(btn(label("Prev", "‹"), busy || !c.hasPrev, () => c.prevPage(), "Vorherige Seite"));

      const pos = document.createElement("span");
      pos.className = "pg-pos";
      const tmpl = host.dataset.template || "Seite {page} von {pages}";
      pos.textContent = tmpl.replace("{page}", String(c.currentPage)).replace("{pages}", String(c.pageCount));
      nav.appendChild(pos);

      nav.appendChild(btn(label("Next", "›"), busy || !c.hasNext, () => c.nextPage(), "Naechste Seite"));
      nav.appendChild(btn(label("Last", "»"), busy || !c.hasNext, () => c.lastPage(), "Letzte Seite"));

      if (pageSizesRaw) {
        const spacer = document.createElement("div");
        spacer.className = "pg-spacer";
        nav.appendChild(spacer);
      }

      if (pageSizesRaw) {
        const sizes = pageSizesRaw
          .split(",")
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (sizes.length > 0) {
          const sel = document.createElement("select");
          sel.className = "pg-size";
          sel.setAttribute("aria-label", label("Size", "Eintraege pro Seite"));
          if (!sizes.includes(c.limit)) sizes.unshift(c.limit);
          for (const n of sizes) {
            const opt = document.createElement("option");
            opt.value = String(n);
            opt.textContent = String(n);
            if (n === c.limit) opt.selected = true;
            sel.appendChild(opt);
          }
          sel.addEventListener("change", () => {
            if (!controller || controller.loading) return;
            void controller.setLimit(parseInt(sel.value, 10));
          });
          nav.appendChild(sel);
        }
      }

      box.appendChild(nav);
    };

    Object.defineProperty(host, "controller", {
      configurable: true,
      get: () => controller,
      set: (v: unknown) => {
        unsub?.();
        unsub = null;
        controller = (v as PaginatorController) ?? null;
        if (controller) unsub = controller.subscribe(paint);
        paint();
      },
    });

    if (preController !== undefined) self.controller = preController;

    // Re-Paint bei Attribut-Aenderung (page-sizes/template/labels).
    const obs = new MutationObserver(paint);
    obs.observe(host, { attributes: true });
    ctx.cleanup.push(() => obs.disconnect());
    ctx.cleanup.push(() => unsub?.());

    paint();
  },
});
